import type {
  RiskLevel,
  RiskMatrixScore,
  RiskModelWithDetails,
  RiskTimeHorizon,
} from "@repo/shared/types";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useConfirm } from "@/hooks/use-confirm";
import { ApiError } from "@/lib/api/_client";
import { useUpsertMatrixScore } from "@/lib/hooks/use-risk-models";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./risk-matrix-editor.i18n";

type RiskMatrixEditorProps = {
  model: RiskModelWithDetails;
};

export function RiskMatrixEditor({ model }: RiskMatrixEditorProps) {
  const localize = useLocalize();
  const t = useT(T);

  const upsertScore = useUpsertMatrixScore();

  // Helper to get localized value
  const getLabel = (i18n: Record<string, string>) => localize(i18n);

  const [editingCell, setEditingCell] = useState<{
    timeHorizonId: string;
    riskLevelId: string;
    currentScore: number | null;
  } | null>(null);
  const [scoreInput, setScoreInput] = useState("");
  const { confirm, ConfirmationDialog } = useConfirm();

  const getScore = (timeHorizonId: string, riskLevelId: string): number | null => {
    const score = model.matrixScores.find(
      (s: RiskMatrixScore) => s.timeHorizonId === timeHorizonId && s.riskLevelId === riskLevelId,
    );
    return score?.score ?? null;
  };

  const handleCellClick = (timeHorizonId: string, riskLevelId: string) => {
    const currentScore = getScore(timeHorizonId, riskLevelId);
    setEditingCell({ timeHorizonId, riskLevelId, currentScore });
    setScoreInput(currentScore?.toString() || "");
  };

  const handleSaveScore = async () => {
    if (!editingCell) return;

    const score = parseInt(scoreInput, 10);
    if (Number.isNaN(score) || score < 1 || score > 20) {
      await confirm({
        title: t.validationError,
        description: t.validationMessage,
        confirmText: t.ok,
        type: "info",
      });
      return;
    }

    try {
      await upsertScore.mutateAsync({
        modelId: model.id,
        data: {
          timeHorizonId: editingCell.timeHorizonId,
          riskLevelId: editingCell.riskLevelId,
          score,
        },
      });
      setEditingCell(null);
      setScoreInput("");
    } catch (err) {
      await confirm({
        title: t.error,
        description: err instanceof ApiError ? err.message : t.failedToUpdateScore,
        confirmText: t.ok,
        variant: "destructive",
        type: "alert",
      });
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>{t.riskScoringMatrix}</CardTitle>
          <p className="text-sm text-muted-foreground">{t.matrixDescription}</p>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className="border p-3 bg-muted text-left font-semibold">
                    {t.riskLevelTimeHorizon}
                  </th>
                  {model.timeHorizons.map((horizon: RiskTimeHorizon) => (
                    <th
                      key={horizon.id}
                      className="border p-3 bg-muted text-center font-semibold min-w-[120px]"
                    >
                      {getLabel(horizon.labelI18n)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {model.riskLevels.map((level: RiskLevel) => (
                  <tr key={level.id}>
                    <td className="border p-3 font-medium bg-muted">{getLabel(level.labelI18n)}</td>
                    {model.timeHorizons.map((horizon: RiskTimeHorizon) => {
                      const score = getScore(horizon.id, level.id);

                      // QL-650 — scores are plain numbers: no risk-tier colour.
                      return (
                        <td
                          key={`${horizon.id}-${level.id}`}
                          className="border p-3 text-center cursor-pointer hover:bg-muted/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                          onClick={() => handleCellClick(horizon.id, level.id)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleCellClick(horizon.id, level.id);
                            }
                          }}
                          /* biome-ignore lint/a11y/noNoninteractiveTabindex: intentional interactive table cell for risk matrix editing */
                          tabIndex={0}
                          aria-label={`${t.riskScoreAriaLabel} ${getLabel(horizon.labelI18n)} - ${getLabel(level.labelI18n)}`}
                        >
                          {score !== null ? (
                            <span className="text-2xl font-bold">{score}</span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!editingCell} onOpenChange={() => setEditingCell(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.setRiskScore}</DialogTitle>
            <DialogDescription>{t.enterScoreDescription}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground mb-2">{t.enterScoreHint}</p>
              <Input
                type="number"
                min="1"
                max="20"
                value={scoreInput}
                onChange={(e) => setScoreInput(e.target.value)}
                placeholder={t.scorePlaceholder}
                autoFocus
              />
            </div>

            {editingCell && editingCell.currentScore !== null && (
              <p className="text-sm text-muted-foreground">
                {t.currentScore} {editingCell.currentScore}
              </p>
            )}
          </div>

          <DialogFooter>
            <DialogCancel>{t.cancel}</DialogCancel>
            <Button onClick={handleSaveScore}>{t.save}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmationDialog />
    </>
  );
}
